// UltraFlex Admin — EPOS Till Configs Generator
// Head-office tool: pick a site + enter printer/PIN details, click
// "Download config.json" — the downloaded file is ready to drop into
// %APPDATA%/ultraflex-epos/ultraflex-epos-config.json on the target till PC.
// That skips the in-app setup wizard completely.

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { MapPin, Printer, KeyRound, Download, Info, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminEposConfigs() {
  const { token } = useAuth();
  const [sites, setSites] = useState([]);
  const [form, setForm] = useState({
    backend_url: API || '',
    site_id: '',
    manager_pin: '',
    confirm_pin: '',
    printer_enabled: false,
    printer_type: 'epson',
    printer_interface: '',
    printer_width: 48,
    auto_print_receipts: true,
    kick_drawer_after_sale: true,
  });
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetch(`${API}/api/sites`, { headers }).then(r => r.ok ? r.json() : []).then(d => {
      const all = (d.items || d || []).filter(s => s.site_type !== 'warehouse');
      setSites(all);
    });
    // eslint-disable-next-line
  }, []);

  const validate = () => {
    if (!form.backend_url.trim()) return 'Backend URL is required';
    if (!form.site_id) return 'Pick a site';
    if (!form.manager_pin || form.manager_pin.length < 4) return 'Manager PIN must be 4-6 digits';
    if (form.manager_pin !== form.confirm_pin) return 'PINs do not match';
    if (form.manager_pin === '9999') return 'PIN cannot be 9999 (default)';
    if (form.printer_enabled && !form.printer_interface.trim()) return 'Printer enabled — interface is required';
    return null;
  };

  const generate = async () => {
    setErr(''); setOk(''); setPreview(null);
    const v = validate();
    if (v) { setErr(v); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/epos/till-configs/generate`, {
        method: 'POST', headers,
        body: JSON.stringify({
          backend_url: form.backend_url.trim(),
          site_id: form.site_id,
          manager_pin: form.manager_pin,
          printer_enabled: form.printer_enabled,
          printer_type: form.printer_type,
          printer_interface: form.printer_interface.trim(),
          printer_width: form.printer_width,
          auto_print_receipts: form.auto_print_receipts,
          kick_drawer_after_sale: form.kick_drawer_after_sale,
        }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || 'Failed'); }
      const cfg = await res.json();
      setPreview(cfg);
      setOk('Config generated — click "Download" to save config.json');
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  const download = () => {
    if (!preview) return;
    const site = sites.find(s => s.id === form.site_id);
    const safeName = (site?.name || 'site').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    const blob = new Blob([JSON.stringify(preview, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ultraflex-epos-config-${safeName}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 min-h-screen bg-uf-bg" data-testid="admin-epos-configs-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold text-white">EPOS Till Configs</h1>
        <p className="text-uf-textMuted text-sm mt-1">Generate a ready-to-drop <code className="text-uf-red">config.json</code> for each till PC. Skip the on-device setup wizard by placing the downloaded file at <code className="text-uf-red">%APPDATA%\ultraflex-epos\ultraflex-epos-config.json</code> on the target machine.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 max-w-6xl">
        {/* Form */}
        <div className="bg-uf-surface border border-uf-border rounded p-5 space-y-4">
          <h2 className="text-white font-bold text-sm uppercase tracking-wider mb-2">Till Details</h2>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-uf-textMuted mb-1">Backend URL</label>
            <input value={form.backend_url} onChange={e => setForm(f => ({ ...f, backend_url: e.target.value }))} data-testid="config-backend-url" placeholder="https://ultraflexgym.co.uk" className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded" />
            <p className="text-uf-textMuted text-[11px] mt-1 flex items-center gap-1"><Info className="w-3 h-3" /> Where the till will call the API. Default: this platform's URL.</p>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-uf-textMuted mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> Site</label>
            <select value={form.site_id} onChange={e => setForm(f => ({ ...f, site_id: e.target.value }))} data-testid="config-site" className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded">
              <option value="">— Select site —</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-uf-textMuted mb-1 flex items-center gap-1"><KeyRound className="w-3 h-3" /> Manager PIN</label>
              <input type="password" maxLength={6} value={form.manager_pin} onChange={e => setForm(f => ({ ...f, manager_pin: e.target.value.replace(/\D/g, '') }))} data-testid="config-pin" placeholder="e.g. 4812" className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-uf-textMuted mb-1">Confirm PIN</label>
              <input type="password" maxLength={6} value={form.confirm_pin} onChange={e => setForm(f => ({ ...f, confirm_pin: e.target.value.replace(/\D/g, '') }))} data-testid="config-pin-confirm" className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded" />
            </div>
          </div>

          <div className="pt-3 border-t border-uf-border">
            <h2 className="text-white font-bold text-sm uppercase tracking-wider mb-3 flex items-center gap-1"><Printer className="w-4 h-4" /> Receipt Printer</h2>
            <label className="flex items-center gap-2 text-white text-sm cursor-pointer mb-3">
              <input type="checkbox" checked={form.printer_enabled} onChange={e => setForm(f => ({ ...f, printer_enabled: e.target.checked }))} data-testid="config-printer-enabled" className="accent-uf-red" />
              Enable receipt printing &amp; cash drawer
            </label>
            {form.printer_enabled && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-uf-textMuted mb-1">Type</label>
                    <select value={form.printer_type} onChange={e => setForm(f => ({ ...f, printer_type: e.target.value }))} data-testid="config-printer-type" className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded">
                      <option value="epson">Epson (TM-T88 / TM-T20)</option>
                      <option value="star">Star (TSP100 etc.)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-uf-textMuted mb-1">Paper width (chars)</label>
                    <select value={form.printer_width} onChange={e => setForm(f => ({ ...f, printer_width: parseInt(e.target.value, 10) }))} data-testid="config-printer-width" className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded">
                      <option value="48">48 (80mm)</option>
                      <option value="32">32 (58mm)</option>
                    </select>
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-xs font-semibold uppercase tracking-wider text-uf-textMuted mb-1">Interface</label>
                  <input value={form.printer_interface} onChange={e => setForm(f => ({ ...f, printer_interface: e.target.value }))} data-testid="config-printer-interface" placeholder="printer:EPSON TM-T20III Receipt" className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded font-mono text-sm" />
                  <p className="text-uf-textMuted text-[11px] mt-1">Exact Windows shared-printer name (<code>printer:Name</code>), <code>USB</code>, or <code>tcp://ip:9100</code>.</p>
                </div>
                <div className="mt-3 space-y-1.5">
                  <label className="flex items-center gap-2 text-white text-sm cursor-pointer">
                    <input type="checkbox" checked={form.auto_print_receipts} onChange={e => setForm(f => ({ ...f, auto_print_receipts: e.target.checked }))} className="accent-uf-red" />
                    Auto-print after every sale
                  </label>
                  <label className="flex items-center gap-2 text-white text-sm cursor-pointer">
                    <input type="checkbox" checked={form.kick_drawer_after_sale} onChange={e => setForm(f => ({ ...f, kick_drawer_after_sale: e.target.checked }))} className="accent-uf-red" />
                    Kick cash drawer on cash payments
                  </label>
                </div>
              </>
            )}
          </div>

          {err && <div className="text-red-400 bg-red-500/10 border border-red-500/30 rounded p-2 text-sm">{err}</div>}
          {ok && <div className="text-green-400 bg-green-500/10 border border-green-500/30 rounded p-2 text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />{ok}</div>}

          <div className="flex gap-2 pt-3 border-t border-uf-border">
            <button onClick={generate} disabled={loading} data-testid="generate-config-btn" className="bg-uf-red hover:bg-uf-red/90 disabled:opacity-50 px-4 py-2 rounded text-white font-medium text-sm">
              {loading ? 'Generating…' : 'Generate config.json'}
            </button>
            {preview && (
              <button onClick={download} data-testid="download-config-btn" className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded text-white font-medium text-sm flex items-center gap-2">
                <Download className="w-4 h-4" /> Download
              </button>
            )}
          </div>
        </div>

        {/* Preview + Instructions */}
        <div className="space-y-4">
          <div className="bg-uf-surface border border-uf-border rounded p-5">
            <h2 className="text-white font-bold text-sm uppercase tracking-wider mb-3">Preview</h2>
            {preview ? (
              <pre className="bg-uf-bg border border-uf-border rounded p-3 text-xs text-green-400 overflow-auto max-h-96" data-testid="config-preview">{JSON.stringify(preview, null, 2)}</pre>
            ) : (
              <p className="text-uf-textMuted text-sm italic">Fill in the form and click "Generate" to see the config.</p>
            )}
          </div>

          <div className="bg-uf-surface border border-uf-border rounded p-5">
            <h2 className="text-white font-bold text-sm uppercase tracking-wider mb-3">Install instructions</h2>
            <ol className="list-decimal list-inside text-sm text-uf-textMuted space-y-2">
              <li>Click <strong className="text-white">Generate</strong> then <strong className="text-white">Download</strong> above.</li>
              <li>On the till PC, install <code className="text-uf-red">UltraFlex-EPOS-Setup.exe</code> (from GitHub Releases).</li>
              <li>Open File Explorer and navigate to:<br /><code className="text-uf-red text-[11px]">%APPDATA%\ultraflex-epos\</code></li>
              <li>Rename the downloaded file to <code className="text-uf-red">ultraflex-epos-config.json</code> and drop it in that folder (replace if exists).</li>
              <li>Launch <strong className="text-white">UltraFlex EPOS</strong> from the desktop. The setup wizard is skipped and the till boots straight into the till select screen.</li>
            </ol>
            <div className="bg-uf-red/10 border border-uf-red/30 rounded p-3 mt-4 text-xs text-uf-red">
              <strong>Keep config.json safe</strong> — it contains the manager PIN. Do not email unencrypted.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
